import { ChangeDetectionStrategy, Component, inject, LOCALE_ID } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SiDatepickerComponent } from '@siemens/element-ng/datepicker';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';

const LOCALE_MAP: Record<string, string> = {
    en: 'en-US',
    de: 'de-DE'
};

/**
 * Reusable AG Grid popup cell editor using the Element design system datepicker.
 * Shows the Element calendar panel directly as a popup below the cell.
 *
 * Register globally via provideSiAgGridConfig or use directly in colDef.cellEditor.
 * Usage in colDef: `{ cellEditor: 'siDateCellEditor' }`
 */
@Component({
    selector: 'app-date-cell-editor',
    imports: [SiDatepickerComponent],
    template: `<si-datepicker [date]="date" [initialFocus]="true" (dateChange)="onDateSelected($event)" />`,
    styles: `
        :host {
            display: block;
        }
    `,
    providers: [
        {
            provide: LOCALE_ID,
            useFactory: () => {
                const lang = inject(TranslateService).getCurrentLang() || 'en';
                return LOCALE_MAP[lang] ?? 'en-US';
            }
        }
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SiDateCellEditorComponent implements ICellEditorAngularComp {
    private params!: ICellEditorParams;
    protected date: Date | undefined;

    agInit(params: ICellEditorParams): void {
        this.params = params;
        this.date = this.parseValue(params.value);
    }

    isPopup(): boolean {
        return true;
    }

    getPopupPosition(): 'over' | 'under' {
        return 'under';
    }

    getValue(): string {
        if (!this.date || isNaN(this.date.getTime())) {
            return '';
        }
        const year = this.date.getFullYear();
        const month = String(this.date.getMonth() + 1).padStart(2, '0');
        const day = String(this.date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    protected onDateSelected(selected: Date): void {
        this.date = selected;
        this.params.stopEditing();
    }

    private parseValue(value: unknown): Date | undefined {
        if (!value) return undefined;
        if (value instanceof Date) return value;
        if (typeof value === 'string') {
            // Parse YYYY-MM-DD as local date to avoid timezone shift
            const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (match) {
                return new Date(+match[1], +match[2] - 1, +match[3]);
            }
            return undefined;
        }
        return undefined;
    }
}
